import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { id: "consultations", label: "Consultations" },
  { id: "contacts", label: "Contact Forms" },
  { id: "chats", label: "Chat Sessions" },
];

const STATUS_OPTIONS = {
  consultations: ["pending", "contacted", "scheduled", "completed", "cancelled"],
  contacts: ["new", "contacted", "resolved", "archived"],
};

const STATUS_COLORS = {
  pending: "bg-amber-100 text-amber-800 border-amber-200",
  new: "bg-blue-100 text-blue-800 border-blue-200",
  contacted: "bg-indigo-100 text-indigo-800 border-indigo-200",
  scheduled: "bg-cyan-100 text-cyan-800 border-cyan-200",
  completed: "bg-emerald-100 text-emerald-800 border-emerald-200",
  resolved: "bg-emerald-100 text-emerald-800 border-emerald-200",
  cancelled: "bg-red-100 text-red-800 border-red-200",
  archived: "bg-gray-100 text-gray-600 border-gray-200",
};

const StatCard = ({ label, value, accent }) => (
  <div data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`} className="bg-white rounded-lg border border-gray-200 p-5">
    <p className="text-sm text-gray-500 mb-1">{label}</p>
    <p className={`text-3xl font-bold ${accent || "text-gray-900"}`}>{value}</p>
  </div>
);

const StatusBadge = ({ status }) => {
  const color = STATUS_COLORS[status] || "bg-gray-100 text-gray-600 border-gray-200";
  return (
    <span className={`inline-block text-xs font-medium px-2.5 py-1 rounded-full border ${color}`}>
      {status || "new"}
    </span>
  );
};

const StatusDropdown = ({ currentStatus, options, onUpdate }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        data-testid="status-dropdown-trigger"
        onClick={() => setOpen(!open)}
        className="text-xs text-gray-500 hover:text-blue-700 underline underline-offset-2 cursor-pointer"
      >
        Change
      </button>
      {open && (
        <div className="absolute right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 py-1 min-w-[140px]">
          {options.map((opt) => (
            <button
              key={opt}
              data-testid={`status-option-${opt}`}
              onClick={() => { onUpdate(opt); setOpen(false); }}
              className={`block w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 ${opt === currentStatus ? "font-semibold text-blue-700" : "text-gray-700"}`}
            >
              {opt}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/* ── Chat Session Viewer ── */
const ChatViewer = ({ sessionId, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/chat-history/${sessionId}`);
        setMessages(data.messages || []);
      } catch { setMessages([]); }
      finally { setLoading(false); }
    })();
  }, [sessionId]);

  return (
    <div data-testid="chat-viewer-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div>
            <h3 className="font-semibold text-gray-900">Chat Session</h3>
            <p className="text-xs text-gray-500 mt-0.5 font-mono">{sessionId}</p>
          </div>
          <button data-testid="chat-viewer-close" onClick={onClose} className="text-gray-400 hover:text-gray-700">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-4 bg-gray-50">
          {loading ? (
            <p className="text-sm text-gray-500 text-center py-8">Loading messages...</p>
          ) : messages.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-8">No messages found.</p>
          ) : messages.map((msg, i) => (
            <div key={i} className="space-y-2">
              <div className="flex justify-end">
                <div className="bg-blue-800 text-white rounded-lg rounded-br-sm px-3 py-2 max-w-[80%] text-sm">{msg.user_message}</div>
              </div>
              <div className="flex justify-start">
                <div className="bg-white border border-gray-200 rounded-lg rounded-bl-sm px-3 py-2 max-w-[80%] text-sm text-gray-800 shadow-sm">{msg.ai_response}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ── Main Dashboard ── */
const AdminDashboard = () => {
  const [tab, setTab] = useState("consultations");
  const [stats, setStats] = useState(null);
  const [consultations, setConsultations] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [chatSessions, setChatSessions] = useState([]);
  const [viewingChat, setViewingChat] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, consRes, contactsRes, chatsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/consultation-requests`),
        axios.get(`${API}/contact-forms`),
        axios.get(`${API}/chat-sessions`),
      ]);
      setStats(statsRes.data);
      setConsultations(consRes.data.consultation_requests || []);
      setContacts(contactsRes.data.contact_forms || []);
      setChatSessions(chatsRes.data.chat_sessions || []);
    } catch (e) { console.error("Admin fetch error", e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const updateConsultationStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/consultation-requests/${id}/status`, { status });
      setConsultations((prev) => prev.map((c) => (c.id === id ? { ...c, status } : c)));
      setStats((s) => s && { ...s, pending_consultations: consultations.filter((c) => c.id !== id && c.status === "pending").length + (status === "pending" ? 1 : 0) });
    } catch (e) { console.error("Update error", e); }
  };

  const updateContactStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/contact-forms/${id}/status`, { status });
      setContacts((prev) => prev.map((c) => (c.id === id ? { ...c, status } : c)));
    } catch (e) { console.error("Update error", e); }
  };

  const fmt = (ts) => {
    if (!ts) return "—";
    const d = new Date(ts);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) + " " + d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Title */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
          <p className="text-sm text-gray-500 mt-1">Manage consultation requests, contact submissions, and chat sessions</p>
        </div>

        {/* Stats */}
        {stats && (
          <div data-testid="admin-stats" className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
            <StatCard label="Total Consultations" value={stats.total_consultations} accent="text-blue-700" />
            <StatCard label="Pending Consultations" value={stats.pending_consultations} accent="text-amber-600" />
            <StatCard label="Total Contacts" value={stats.total_contacts} accent="text-blue-700" />
            <StatCard label="Pending Contacts" value={stats.pending_contacts} accent="text-amber-600" />
            <StatCard label="Chat Messages" value={stats.total_chat_messages} accent="text-indigo-600" />
          </div>
        )}

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="flex space-x-6" data-testid="admin-tabs">
            {TABS.map((t) => (
              <button
                key={t.id}
                data-testid={`tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                  tab === t.id ? "border-blue-700 text-blue-700" : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                {t.label}
                <span className="ml-1.5 text-xs bg-gray-100 text-gray-600 rounded-full px-2 py-0.5">
                  {t.id === "consultations" ? consultations.length : t.id === "contacts" ? contacts.length : chatSessions.length}
                </span>
              </button>
            ))}
          </nav>
        </div>

        {loading ? (
          <div className="text-center py-16 text-gray-500">Loading...</div>
        ) : (
          <>
            {/* Consultations Tab */}
            {tab === "consultations" && (
              <div data-testid="consultations-table" className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-left text-xs text-gray-500 uppercase tracking-wider">
                      <tr>
                        <th className="px-4 py-3">Name</th>
                        <th className="px-4 py-3">Company</th>
                        <th className="px-4 py-3">Email</th>
                        <th className="px-4 py-3">Phone</th>
                        <th className="px-4 py-3">Service</th>
                        <th className="px-4 py-3">Date / Time</th>
                        <th className="px-4 py-3">Status</th>
                        <th className="px-4 py-3">Submitted</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {consultations.length === 0 ? (
                        <tr><td colSpan={8} className="text-center py-10 text-gray-400">No consultation requests yet.</td></tr>
                      ) : consultations.map((c) => (
                        <tr key={c.id} data-testid={`consultation-row-${c.id}`} className="hover:bg-gray-50">
                          <td className="px-4 py-3 font-medium text-gray-900">{c.name}</td>
                          <td className="px-4 py-3 text-gray-600">{c.company || "—"}</td>
                          <td className="px-4 py-3 text-gray-600">{c.email}</td>
                          <td className="px-4 py-3 text-gray-600">{c.phone || "—"}</td>
                          <td className="px-4 py-3 text-gray-600">{c.service || "—"}</td>
                          <td className="px-4 py-3 text-gray-600">{c.preferred_date || "—"}{c.preferred_time ? ` ${c.preferred_time}` : ""}</td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <StatusBadge status={c.status} />
                              <StatusDropdown currentStatus={c.status} options={STATUS_OPTIONS.consultations} onUpdate={(s) => updateConsultationStatus(c.id, s)} />
                            </div>
                          </td>
                          <td className="px-4 py-3 text-gray-500 text-xs">{fmt(c.timestamp)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Contacts Tab */}
            {tab === "contacts" && (
              <div data-testid="contacts-table" className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-left text-xs text-gray-500 uppercase tracking-wider">
                      <tr>
                        <th className="px-4 py-3">Name</th>
                        <th className="px-4 py-3">Email</th>
                        <th className="px-4 py-3">Phone</th>
                        <th className="px-4 py-3">Company</th>
                        <th className="px-4 py-3">Service Interest</th>
                        <th className="px-4 py-3">Message</th>
                        <th className="px-4 py-3">Status</th>
                        <th className="px-4 py-3">Submitted</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {contacts.length === 0 ? (
                        <tr><td colSpan={8} className="text-center py-10 text-gray-400">No contact submissions yet.</td></tr>
                      ) : contacts.map((c) => (
                        <tr key={c.id} data-testid={`contact-row-${c.id}`} className="hover:bg-gray-50">
                          <td className="px-4 py-3 font-medium text-gray-900">{c.name}</td>
                          <td className="px-4 py-3 text-gray-600">{c.email}</td>
                          <td className="px-4 py-3 text-gray-600">{c.phone || "—"}</td>
                          <td className="px-4 py-3 text-gray-600">{c.company || "—"}</td>
                          <td className="px-4 py-3 text-gray-600">{c.service_interest || "—"}</td>
                          <td className="px-4 py-3 text-gray-600 max-w-xs truncate">{c.message}</td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <StatusBadge status={c.status || "new"} />
                              <StatusDropdown currentStatus={c.status || "new"} options={STATUS_OPTIONS.contacts} onUpdate={(s) => updateContactStatus(c.id, s)} />
                            </div>
                          </td>
                          <td className="px-4 py-3 text-gray-500 text-xs">{fmt(c.timestamp)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Chat Sessions Tab */}
            {tab === "chats" && (
              <div data-testid="chats-table" className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-left text-xs text-gray-500 uppercase tracking-wider">
                      <tr>
                        <th className="px-4 py-3">Session ID</th>
                        <th className="px-4 py-3">First Message</th>
                        <th className="px-4 py-3">Messages</th>
                        <th className="px-4 py-3">Started</th>
                        <th className="px-4 py-3">Last Activity</th>
                        <th className="px-4 py-3">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {chatSessions.length === 0 ? (
                        <tr><td colSpan={6} className="text-center py-10 text-gray-400">No chat sessions yet.</td></tr>
                      ) : chatSessions.map((s) => (
                        <tr key={s.session_id} data-testid={`chat-row-${s.session_id}`} className="hover:bg-gray-50">
                          <td className="px-4 py-3 font-mono text-xs text-gray-700">{s.session_id}</td>
                          <td className="px-4 py-3 text-gray-600 max-w-xs truncate">{s.first_message}</td>
                          <td className="px-4 py-3">
                            <span className="bg-indigo-50 text-indigo-700 text-xs font-medium px-2 py-0.5 rounded-full">{s.message_count}</span>
                          </td>
                          <td className="px-4 py-3 text-gray-500 text-xs">{fmt(s.first_timestamp)}</td>
                          <td className="px-4 py-3 text-gray-500 text-xs">{fmt(s.last_timestamp)}</td>
                          <td className="px-4 py-3">
                            <button
                              data-testid={`view-chat-${s.session_id}`}
                              onClick={() => setViewingChat(s.session_id)}
                              className="text-blue-700 hover:text-blue-900 text-xs font-medium underline underline-offset-2"
                            >
                              View Chat
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Chat Viewer Modal */}
      {viewingChat && <ChatViewer sessionId={viewingChat} onClose={() => setViewingChat(null)} />}
    </div>
  );
};

export default AdminDashboard;
